import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { FormHeader } from "../FormHeader";
import { FormFooter } from "../FormFooter";

const textAreaVariants = cva(
  "relative flex flex-col border rounded-large transition-all font-functional font-size-100 leading-100",
  {
    variants: {
      size: {
        small: "p-3 min-h-[56px] text-xs gap-2",
        medium: "p-4 min-h-[56px] text-sm gap-2",
        large: "p-5 min-h-[64px]text-base gap-3",
      },
      validationState: {
        none: `
        border-action-outline-neutral-faded 
        hover:border-action-outline-neutral-faded-hover 
        hover:bg-action-fill-neutral-faded-hover
        focus-within:border-action-outline-primary-default 
        focus-within:bg-white!
        focus-within:ring-2 
        ring-surface-outline-primary-muted`,
        positive: `
          border-action-outline-positive-default 
          focus-within:border-action-outline-positive-hover
          focus-within:ring-2
          ring-action-outline-positive-faded-hover`,
        negative: `border-action-outline-negative-default 
          focus-within:border-action-outline-negative-hover
          focus-within:ring-2 
          ring-action-outline-negative-faded-hover`,
      },
      isDisabled: {
        true: `
        border
        border-action-outline-neutral-disabled
        hover:border-action-outline-neutral-disabled
        bg-surface-fill-neutral-intense 
        hover:bg-surface-fill-neutral-intense 
        cursor-not-allowed`,
        false: "",
      },
    },
    defaultVariants: {
      size: "medium",
      validationState: "none",
      isDisabled: false,
    },
  }
);

export interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
  label?: string;
  helperText?: string;
  errorText?: string;
  successText?: string;
  size?: "small" | "medium" | "large";
  validationState?: "none" | "positive" | "negative";
  isDisabled?: boolean;
  isRequired?: boolean;
  isOptional?: boolean;
  maxChar?: number;
  showCharCount?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  textAreaClassName?: string;
  infoHeading?: string;
  infoDescription?: string;
  LinkComponent?: React.ReactNode;
  linkText?: string;
  linkHref?: string;
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  rows?: number;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      helperText,
      errorText,
      successText,
      size = "medium",
      validationState = "none",
      isDisabled = false,
      isRequired = false,
      isOptional = false,
      maxChar,
      showCharCount = true,
      infoDescription,
      infoHeading,
      LinkComponent,
      linkText,
      linkHref,
      onLinkClick,
      containerClassName,
      labelClassName,
      textAreaClassName,
      className,
      value,
      onChange,
      rows = 4,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState("");
    const textAreaValue = value !== undefined ? value : internalValue;
    const currentLength = String(textAreaValue).length;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;

      // Prevent exceeding maxChar if specified
      if (maxChar && newValue.length > maxChar) {
        return;
      }

      if (onChange) {
        onChange(e);
      } else {
        setInternalValue(newValue);
      }
    };

    // Determine which helper text to show
    const displayHelperText = errorText || successText || helperText;
    const currentValidationState = errorText
      ? "negative"
      : successText
      ? "positive"
      : validationState;

    // Check if we're approaching or at the limit
    const isNearLimit = maxChar && currentLength >= maxChar * 0.9;
    const isAtLimit = maxChar && currentLength >= maxChar;

    const sizeConfig = {
      small: {
        gap: "gap-2",
      },
      medium: {
        gap: "gap-2",
      },
      large: {
        gap: "gap-3",
      },
    };

    return (
      <div
        className={cn(
          "w-full flex flex-col",
          sizeConfig[size].gap,
          containerClassName
        )}
      >
        {label && (
          <FormHeader
            label={label}
            size={size}
            isRequired={isRequired}
            isOptional={isOptional}
            infoHeading={infoHeading}
            infoDescription={infoDescription}
            LinkComponent={LinkComponent}
            linkText={linkText}
            linkHref={linkHref}
            onLinkClick={onLinkClick}
            htmlFor={props.id}
            className="mb-2"
            labelClassName={labelClassName}
          />
        )}

        <div
          className={cn(
            textAreaVariants({
              size,
              validationState: currentValidationState,
              isDisabled,
            }),
            className
          )}
        >
          <textarea
            ref={ref}
            value={textAreaValue}
            onChange={handleChange}
            disabled={isDisabled}
            required={isRequired}
            rows={rows}
            className={cn(
              "flex-1 bg-transparent border-none outline-none text-surface-ink-neutral-normal placeholder:text-surface-ink-neutral-muted disabled:cursor-not-allowed disabled:text-surface-ink-neutral-disabled resize-none",
              size === "small" && "text-xs",
              size === "medium" && "text-sm",
              size === "large" && "text-base",
              textAreaClassName
            )}
            {...props}
          />
        </div>
        <FormFooter
          helperText={displayHelperText}
          trailingText={
            maxChar && showCharCount ? `${currentLength}/${maxChar}` : undefined
          }
          validationState={
            currentValidationState === "none"
              ? "default"
              : currentValidationState
          }
          size={size}
          isDisabled={isDisabled}
          className="mt-1"
          trailingTextClassName={cn(
            "transition-colors",
            isAtLimit
              ? "text-feedback-ink-negative-subtle"
              : isNearLimit
              ? "text-feedback-ink-warning-subtle"
              : ""
          )}
        />
      </div>
    );
  }
);

TextArea.displayName = "TextArea";

export { TextArea, textAreaVariants };
